<template>
    <div>
      <h2 class="subtitle">Trip Reports</h2>
      
      <div class="columns">
        <!-- Policy Violation Trends -->
        <div class="column is-half">
          <div class="box">
            <h3 class="is-size-5 mb-3">Policy Violation Trends</h3>
            <div v-if="loading" class="has-text-centered py-6">
              <b-loading :is-full-page="false" :active="true"></b-loading>
            </div>
            <div v-else-if="!policyViolationTrendData.labels" class="has-text-centered py-6">
              <span class="icon is-large">
                <i class="fas fa-chart-line fa-3x has-text-grey-light"></i>
              </span>
              <p class="mt-3 has-text-grey">Insufficient data to display trend chart.</p>
            </div>
            <div v-else class="chart-container" style="height: 300px;">
              <canvas ref="policyTrendChart"></canvas>
            </div>
            <div class="mt-3">
              <p class="has-text-weight-bold">Summary:</p>
              <p>{{ policyViolationTrendSummary }}</p>
            </div>
          </div>
        </div>
        
        <!-- Frequent Policy Violators -->
        <div class="column is-half">
          <div class="box">
            <h3 class="is-size-5 mb-3">Frequent Policy Violators</h3>
            <div v-if="loading" class="has-text-centered py-6">
              <b-loading :is-full-page="false" :active="true"></b-loading>
            </div>
            <div v-else-if="!frequentViolators.length" class="has-text-centered py-6">
              <span class="icon is-large">
                <i class="fas fa-users fa-3x has-text-grey-light"></i>
              </span>
              <p class="mt-3 has-text-grey">No frequent violators to display.</p>
            </div>
            <div v-else>
              <b-table
                :data="frequentViolators"
                :striped="true"
                :hoverable="true"
                :paginated="frequentViolators.length > 5"
                :per-page="5"
                default-sort="violationCount"
                default-sort-direction="desc"
              >
                <b-table-column field="travelerName" label="Traveler" sortable v-slot="props">
                  {{ props.row.travelerName }}
                </b-table-column>
                <b-table-column field="violationCount" label="Violations" numeric centered sortable v-slot="props">
                  <b-tag :type="getViolationCountType(props.row.violationCount)">
                    {{ props.row.violationCount }}
                  </b-tag>
                </b-table-column>
                <b-table-column field="commonViolation" label="Most Common Violation" v-slot="props">
                  {{ props.row.commonViolation }}
                </b-table-column>
                <b-table-column field="lastViolation" label="Last Violation" centered sortable v-slot="props">
                  {{ formatDate(props.row.lastViolation) }}
                </b-table-column>
              </b-table>
            </div>
          </div>
        </div>
      </div>
      
      <div class="columns mt-4">
        <!-- Most Common Policy Violations -->
        <div class="column is-full">
          <div class="box">
            <h3 class="is-size-5 mb-3">Most Common Policy Violations</h3>
            <div v-if="loading" class="has-text-centered py-6">
              <b-loading :is-full-page="false" :active="true"></b-loading>
            </div>
            <div v-else-if="!commonViolations.length" class="has-text-centered py-6">
              <span class="icon is-large">
                <i class="fas fa-exclamation-triangle fa-3x has-text-grey-light"></i>
              </span>
              <p class="mt-3 has-text-grey">No policy violations to display.</p>
            </div>
            <div v-else class="chart-container" style="height: 300px;">
              <canvas ref="commonViolationsChart"></canvas>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import moment from "moment";
  import Chart from "chart.js";
  
  export default {
    name: "TripReports",
    props: {
      trips: {
        type: Array,
        required: true
      },
      loading: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        // Chart references
        policyTrendChart: null,
        commonViolationsChart: null,
        
        // Report data
        policyViolationTrendData: {},
        policyViolationTrendSummary: "No significant trends detected in the available data.",
        frequentViolators: [],
        commonViolations: []
      };
    },
    beforeMount() {
      // Make sure we're using correct charts for Chart.js v2
      this.configureChartJs();
    },
    methods: {
      configureChartJs() {
        // For Chart.js v2, we don't need to register components as in v3
        // Chart.js v2 automatically registers all chart types
      },
      formatDate(date) {
        return moment(date).format("YYYY-MM-DD HH:mm");
      },
      
      generateReportsData() {
        this.generatePolicyViolationTrend();
        this.generateFrequentViolators();
        this.generateCommonViolations();
      },
      
      generatePolicyViolationTrend() {
        if (!this.trips || this.trips.length === 0) {
          this.policyViolationTrendData = {};
          return;
        }
        
        // Group trips by month and count policy violations
        const violationsByMonth = {};
        const complianceByMonth = {};
        
        // Get the last 6 months for the chart
        const today = moment();
        const labels = [];
        const violationsData = [];
        const complianceData = [];
        
        // Create labels for the last 6 months
        for (let i = 5; i >= 0; i--) {
          const monthDate = moment().subtract(i, 'months');
          const monthKey = monthDate.format('YYYY-MM');
          labels.push(monthDate.format('MMM YYYY'));
          violationsByMonth[monthKey] = 0;
          complianceByMonth[monthKey] = 0;
        }
        
        // Count violations and compliance by month
        this.trips.forEach(trip => {
          if (!trip.bookedAt) return;
          
          const tripDate = moment(trip.bookedAt);
          const monthKey = tripDate.format('YYYY-MM');
          
          // Only count trips from the last 6 months
          if (violationsByMonth[monthKey] !== undefined) {
            // Check if there are any non-"allTrips" violations
            let hasNonAllTripsViolation = false;
            if (trip.bookingLogPolicy.rulesTriggered && trip.bookingLogPolicy.rulesTriggered.length > 0) {
              for (const rule of trip.bookingLogPolicy.rulesTriggered) {
                if (rule.id !== "allTrips") {
                  hasNonAllTripsViolation = true;
                  break;
                }
              }
            }
            
            if (trip.bookingLogPolicy.triggered === 'Y' && hasNonAllTripsViolation) {
              violationsByMonth[monthKey]++;
            } else {
              complianceByMonth[monthKey]++;
            }
          }
        });
        
        // Prepare data for chart
        Object.keys(violationsByMonth).sort().forEach(month => {
          violationsData.push(violationsByMonth[month]);
          complianceData.push(complianceByMonth[month]);
        });
        
        // Store the data
        this.policyViolationTrendData = {
          labels,
          violationsData,
          complianceData
        };
        
        // Generate summary text
        this.generateTrendSummary(violationsData);
        
        // Draw the chart
        this.$nextTick(() => {
          this.drawPolicyTrendChart();
        });
      },
      
      generateFrequentViolators() {
        if (!this.trips || this.trips.length === 0) {
          this.frequentViolators = [];
          return;
        }
        
        // Group by traveler and count violations
        const travelerViolations = {};
        
        this.trips.forEach(trip => {
          if (trip.bookingLogPolicy.triggered !== 'Y') return;
          
          const travelerName = `${trip.traveler.firstName} ${trip.traveler.lastName}`;
          
          if (!travelerViolations[travelerName]) {
            travelerViolations[travelerName] = {
              travelerName,
              violationCount: 0,
              lastViolation: null,
              violations: {}
            };
          }
          
          // Count the number of non-"allTrips" violations
          let hasNonAllTripsViolation = false;
          if (trip.bookingLogPolicy.rulesTriggered && trip.bookingLogPolicy.rulesTriggered.length > 0) {
            trip.bookingLogPolicy.rulesTriggered.forEach(rule => {
              // Skip the "allTrips" rule
              if (rule.id === "allTrips") return;
              
              hasNonAllTripsViolation = true;
              
              if (!travelerViolations[travelerName].violations[rule.id]) {
                travelerViolations[travelerName].violations[rule.id] = {
                  message: rule.message,
                  count: 0
                };
              }
              travelerViolations[travelerName].violations[rule.id].count++;
            });
          }
          
          // Only increment the violation count if there was at least one non-"allTrips" violation
          if (hasNonAllTripsViolation) {
            travelerViolations[travelerName].violationCount++;
            
            // Track the latest violation date
            const tripDate = moment(trip.bookedAt);
            if (!travelerViolations[travelerName].lastViolation || 
                tripDate.isAfter(moment(travelerViolations[travelerName].lastViolation))) {
              travelerViolations[travelerName].lastViolation = trip.bookedAt;
            }
          }
        });
        
        // Convert to array and sort by violation count
        const violatorsArray = Object.values(travelerViolations);
        
        // Find most common violation for each traveler
        violatorsArray.forEach(traveler => {
          let maxCount = 0;
          let commonViolation = 'Unknown';
          
          Object.values(traveler.violations).forEach(violation => {
            if (violation.count > maxCount) {
              maxCount = violation.count;
              commonViolation = violation.message;
            }
          });
          
          traveler.commonViolation = commonViolation;
        });
        
        // Sort by violation count (descending)
        violatorsArray.sort((a, b) => b.violationCount - a.violationCount);
        
        // Take top 10 violators
        this.frequentViolators = violatorsArray.slice(0, 10);
      },
      
      generateCommonViolations() {
        if (!this.trips || this.trips.length === 0) {
          this.commonViolations = [];
          return;
        }
        
        // Count violation types
        const violationTypes = {};
        
        this.trips.forEach(trip => {
          if (trip.bookingLogPolicy.rulesTriggered && trip.bookingLogPolicy.rulesTriggered.length > 0) {
            trip.bookingLogPolicy.rulesTriggered.forEach(rule => {
              // Skip the "allTrips" rule as requested
              if (rule.id === "allTrips") return;
              
              if (!violationTypes[rule.id]) {
                violationTypes[rule.id] = {
                  id: rule.id,
                  message: rule.message,
                  count: 0
                };
              }
              violationTypes[rule.id].count++;
            });
          }
        });
        
        // Convert to array and sort by count
        const violationsArray = Object.values(violationTypes);
        violationsArray.sort((a, b) => b.count - a.count);
        
        // Take top 10 violations
        this.commonViolations = violationsArray.slice(0, 10);
        
        // Draw the chart
        this.$nextTick(() => {
          this.drawCommonViolationsChart();
        });
      },
      
      generateTrendSummary(violationsData) {
        if (!violationsData || violationsData.length < 2) {
          this.policyViolationTrendSummary = "Insufficient data to analyze trends.";
          return;
        }
        
        // Simple trend analysis
        let increasing = 0;
        let decreasing = 0;
        
        for (let i = 1; i < violationsData.length; i++) {
          if (violationsData[i] > violationsData[i-1]) {
            increasing++;
          } else if (violationsData[i] < violationsData[i-1]) {
            decreasing++;
          }
        }
        
        if (increasing > decreasing) {
          this.policyViolationTrendSummary = `Policy violations have been trending upward over the last ${violationsData.length} months.`;
        } else if (decreasing > increasing) {
          this.policyViolationTrendSummary = `Policy violations have been trending downward over the last ${violationsData.length} months, indicating improved compliance.`;
        } else {
          this.policyViolationTrendSummary = `Policy violations have remained relatively stable over the last ${violationsData.length} months.`;
        }
      },
      
      drawPolicyTrendChart() {
        const ctx = this.$refs.policyTrendChart;
        if (!ctx || !this.policyViolationTrendData.labels) return;
        
        // Destroy previous chart if it exists
        if (this.policyTrendChart) {
          this.policyTrendChart.destroy();
        }
        
        this.policyTrendChart = new Chart(ctx, {
          type: 'line',
          data: {
            labels: this.policyViolationTrendData.labels,
            datasets: [
              {
                label: 'Policy Violations',
                data: this.policyViolationTrendData.violationsData,
                borderColor: '#ff3860',
                backgroundColor: 'rgba(255, 56, 96, 0.1)',
                borderWidth: 2,
                lineTension: 0.3,
                fill: true
              },
              {
                label: 'Policy Compliant',
                data: this.policyViolationTrendData.complianceData,
                borderColor: '#23d160',
                backgroundColor: 'rgba(35, 209, 96, 0.1)',
                borderWidth: 2,
                lineTension: 0.3,
                fill: true
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              yAxes: [{
                ticks: {
                  beginAtZero: true
                },
                scaleLabel: {
                  display: true,
                  labelString: 'Number of Trips'
                }
              }],
              xAxes: [{
                scaleLabel: {
                  display: true,
                  labelString: 'Month'
                }
              }]
            },
            tooltips: {
              mode: 'index',
              intersect: false
            },
            legend: {
              position: 'top',
            },
            title: {
              display: true,
              text: 'Policy Compliance Trend'
            }
          }
        });
      },
      
      drawCommonViolationsChart() {
        const ctx = this.$refs.commonViolationsChart;
        if (!ctx || !this.commonViolations.length) return;
        
        // Destroy previous chart if it exists
        if (this.commonViolationsChart) {
          this.commonViolationsChart.destroy();
        }
        
        // Prepare data
        const labels = this.commonViolations.map(v => v.message);
        const data = this.commonViolations.map(v => v.count);
        const backgroundColor = [
          '#ff3860', '#3273dc', '#ffdd57', '#23d160', '#209cee',
          '#6c5ce7', '#00d1b2', '#ff9f43', '#ee5253', '#2e86de'
        ];
        
        this.commonViolationsChart = new Chart(ctx, {
          type: 'bar',
          data: {
            labels,
            datasets: [{
              label: 'Violation Count',
              data,
              backgroundColor,
              borderColor: 'rgba(255, 255, 255, 0.5)',
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              yAxes: [{
                ticks: {
                  beginAtZero: true
                },
                scaleLabel: {
                  display: true,
                  labelString: 'Occurrence Count'
                }
              }],
              xAxes: [{
                scaleLabel: {
                  display: true,
                  labelString: 'Policy Violation Type'
                }
              }]
            },
            legend: {
              display: false
            },
            title: {
              display: true,
              text: 'Most Common Policy Violations'
            }
          }
        });
      },
      
      getViolationCountType(count) {
        if (count >= 5) return 'is-danger';
        if (count >= 3) return 'is-warning';
        return 'is-info';
      }
    },
    watch: {
      trips: {
        handler() {
          this.generateReportsData();
        },
        immediate: true,
        deep: true
      }
    },
    beforeDestroy() {
      // Clean up charts when component is destroyed
      if (this.policyTrendChart) {
        this.policyTrendChart.destroy();
      }
      if (this.commonViolationsChart) {
        this.commonViolationsChart.destroy();
      }
    }
  };
  </script>
  
  <style scoped>
  .chart-container {
    position: relative;
    width: 100%;
  }
  
  .box {
    margin-bottom: 1.5rem;
  }
  </style>